/**
 * OntologyMiniGraph — dependency-free SVG visualisation of an ontology
 * version's entity / relation graph.
 *
 * Layout: deterministic radial placement (entities on a circle) so the
 * picture is stable across reloads (no force-sim wobble) and the same
 * payload always renders identically. Good enough for ~10-40 entities;
 * for larger graphs callers can pre-trim to "top N by degree".
 *
 * Data shape matches `GET /api/v1/ontology/versions/<id>/graph/`:
 *   { nodes: [{id, label, parent?, description?}],
 *     edges: [{id, label, source, target, cardinality?}] }
 */

interface Node { id: string; label?: string }
interface Edge { id?: string; label: string; source: string; target: string }

export interface OntologyGraph {
  nodes: Node[];
  edges: Edge[];
}

interface Props {
  graph: OntologyGraph;
  /** Canvas height in px. Width is fluid via viewBox. */
  height?: number;
  /** Max entities to render (truncated by insertion order). */
  maxNodes?: number;
}

export function OntologyMiniGraph({ graph, height = 360, maxNodes = 40 }: Props) {
  const nodes = (graph.nodes ?? []).slice(0, maxNodes);
  const allowed = new Set(nodes.map((n) => n.id));
  const edges = (graph.edges ?? []).filter(
    (e) => allowed.has(e.source) && allowed.has(e.target)
  );

  if (nodes.length === 0) {
    return (
      <div className="rounded border border-border bg-bg/50 p-6 text-center text-xs text-muted">
        No entities to visualise yet.
      </div>
    );
  }

  const W = 800;
  const H = height;
  const cx = W / 2;
  const cy = H / 2;
  const radius = Math.min(W, H) * 0.36;

  const pos = new Map<string, { x: number; y: number }>();
  nodes.forEach((n, i) => {
    if (nodes.length === 1) { pos.set(n.id, { x: cx, y: cy }); return; }
    const angle = (i / nodes.length) * Math.PI * 2 - Math.PI / 2;
    pos.set(n.id, { x: cx + Math.cos(angle) * radius, y: cy + Math.sin(angle) * radius });
  });

  // Degree drives node size so hub entities read as more important.
  const degree = new Map<string, number>();
  edges.forEach((e) => {
    degree.set(e.source, (degree.get(e.source) ?? 0) + 1);
    degree.set(e.target, (degree.get(e.target) ?? 0) + 1);
  });
  const radiusFor = (id: string) => 22 + Math.min(10, (degree.get(id) ?? 0) * 2);

  const selfLoops = edges.filter((e) => e.source === e.target);
  const links     = edges.filter((e) => e.source !== e.target);

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-gradient-to-b from-panel/60 to-bg/40">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block h-auto w-full"
        role="img"
        aria-label="Ontology graph"
      >
        <defs>
          <radialGradient id="omg-node" cx="35%" cy="30%" r="80%">
            <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity="0.55" />
            <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity="0.12" />
          </radialGradient>
          <radialGradient id="omg-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity="0.10" />
            <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity="0" />
          </radialGradient>
          <marker id="omg-arrow" viewBox="0 0 10 10" refX="8" refY="5"
                  markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" className="fill-accent" opacity={0.8} />
          </marker>
          <filter id="omg-shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="1" stdDeviation="2"
                          floodColor="hsl(var(--accent))" floodOpacity="0.25" />
          </filter>
        </defs>

        {/* Ambient glow */}
        <circle cx={cx} cy={cy} r={radius * 1.25} fill="url(#omg-glow)" />

        {/* Edges (curved) */}
        <g fill="none">
          {links.map((e, i) => {
            const a = pos.get(e.source)!;
            const b = pos.get(e.target)!;
            const ra = radiusFor(e.source);
            const rb = radiusFor(e.target);
            const dx = b.x - a.x, dy = b.y - a.y;
            const len = Math.hypot(dx, dy) || 1;
            const ux = dx / len, uy = dy / len;
            const x1 = a.x + ux * ra, y1 = a.y + uy * ra;
            const x2 = b.x - ux * (rb + 6), y2 = b.y - uy * (rb + 6);
            // Gentle arc: control point offset perpendicular to the chord.
            const mx = (x1 + x2) / 2 + (-uy) * 26;
            const my = (y1 + y2) / 2 + (ux) * 26;
            return (
              <path key={i} d={`M ${x1} ${y1} Q ${mx} ${my} ${x2} ${y2}`}
                    className="stroke-border" strokeWidth="1.5"
                    markerEnd="url(#omg-arrow)" opacity={0.85} />
            );
          })}
          {selfLoops.map((e, i) => {
            const a = pos.get(e.source)!;
            const r = radiusFor(e.source);
            return (
              <path key={`l${i}`}
                    d={`M ${a.x + r} ${a.y - 6} a 16 16 0 1 1 -2 16`}
                    className="stroke-border" strokeWidth="1.5"
                    markerEnd="url(#omg-arrow)" opacity={0.85} />
            );
          })}
        </g>

        {/* Edge labels as chips */}
        <g>
          {links.map((e, i) => {
            const a = pos.get(e.source)!;
            const b = pos.get(e.target)!;
            const ux = (b.x - a.x), uy = (b.y - a.y);
            const len = Math.hypot(ux, uy) || 1;
            const mx = (a.x + b.x) / 2 + (-uy / len) * 13;
            const my = (a.y + b.y) / 2 + (ux / len) * 13;
            const txt = e.label.length > 20 ? e.label.slice(0, 19) + "…" : e.label;
            const w = txt.length * 5.6 + 12;
            return (
              <g key={i} transform={`translate(${mx} ${my})`}>
                <rect x={-w / 2} y={-8} width={w} height={16} rx={8}
                      className="fill-panel stroke-border" strokeWidth="1" opacity={0.95} />
                <text textAnchor="middle" dominantBaseline="central"
                      fontSize="9.5" className="fill-muted font-medium tracking-wide">
                  {txt}
                </text>
              </g>
            );
          })}
        </g>

        {/* Nodes */}
        <g>
          {nodes.map((n) => {
            const p = pos.get(n.id)!;
            const r = radiusFor(n.id);
            const label = (n.label ?? n.id);
            const shown = label.length > 16 ? label.slice(0, 15) + "…" : label;
            return (
              <g key={n.id} transform={`translate(${p.x} ${p.y})`}>
                <circle r={r + 4} className="fill-none stroke-accent" strokeWidth="1" opacity={0.25} />
                <circle r={r} fill="url(#omg-node)" className="stroke-accent"
                        strokeWidth="1.5" filter="url(#omg-shadow)" />
                <text textAnchor="middle" dominantBaseline="central"
                      fontSize="11.5" className="font-semibold"
                      style={{
                        fill: "hsl(var(--text))",
                        paintOrder: "stroke",
                        stroke: "hsl(var(--bg))",
                        strokeWidth: 3,
                        strokeLinejoin: "round",
                      }}>
                  {shown}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
      <div className="flex items-center justify-between border-t border-border px-3 py-1.5 text-[11px] text-muted">
        <span>
          {nodes.length} entit{nodes.length === 1 ? "y" : "ies"} ·{" "}
          {edges.length} relation{edges.length === 1 ? "" : "s"}
          {graph.nodes && graph.nodes.length > nodes.length && (
            <span> · showing first {nodes.length} of {graph.nodes.length}</span>
          )}
        </span>
        <span className="hidden items-center gap-1.5 sm:flex">
          <span className="inline-block h-2 w-2 rounded-full bg-accent/60" /> entity
          <span className="ml-2 inline-block h-px w-4 bg-border" /> relationship
        </span>
      </div>
    </div>
  );
}

